import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";

// ─── Constants ───────────────────────────────────────────────────────────────

const SYSTEM_ACTIONS = ["AUTO_APPROVE", "AUTO_DENY", "MANUAL_REVIEW"];

const ACTION_META = {
  AUTO_APPROVE: { label: "Auto-Approve", bg: "#d1fae5", border: "#6ee7b7", text: "#065f46" },
  AUTO_DENY:    { label: "Auto-Deny",    bg: "#fee2e2", border: "#fca5a5", text: "#991b1b" },
  MANUAL_REVIEW:{ label: "Manual Review",bg: "#fef3c7", border: "#fcd34d", text: "#92400e" },
};

const FIELD_TYPES = ["boolean", "select", "multiselect", "number", "attachment", "text"];
const FIELD_TYPE_LABELS = {
  boolean: "Yes / No", select: "Single Select", multiselect: "Multi-Select",
  number: "Number", attachment: "Attachment", text: "Text",
};

const CATEGORY_OPTIONS = ["EmOC", "ECC", "MH", "Other"];

// Code prefix each category maps to — used to filter the service picker
const CATEGORY_PREFIXES = { EmOC: "EMOC", ECC: "ECC", MH: "MH" };
// "Other" has no prefix, shows all services not matched by the named categories
const OPERATORS = [
  { value: "eq", label: "=" }, { value: "ne", label: "≠" },
  { value: "lt", label: "<" }, { value: "lte", label: "≤" },
  { value: "gt", label: ">" }, { value: "gte", label: "≥" },
];

// ─── Network helpers ─────────────────────────────────────────────────────────

const baseUrl = () => {
  try {
    const s = localStorage.getItem("openImisBackendUrl");
    if (s && s.trim()) return s.trim().replace(/\/+$/, "");
  } catch {}
  return window.location.origin;
};

const token = () => {
  try {
    for (const k of Object.keys(localStorage)) {
      const v = localStorage.getItem(k);
      if (v && v.startsWith("Bearer ")) return v.replace("Bearer ", "");
      if (k.toLowerCase().includes("token") && v && v.length > 20) return v;
    }
  } catch {}
  return null;
};

const headers = () => {
  const t = token();
  return t
    ? { "Content-Type": "application/json", Authorization: `Bearer ${t}` }
    : { "Content-Type": "application/json" };
};

const roots = () => {
  const b = baseUrl();
  return [`${b}/api/sync`, `${b}/api`];
};

const tryFetch = async (urls, opts) => {
  for (const url of urls) {
    try {
      const r = await fetch(url, opts);
      if (r.status === 404) continue;
      return r;
    } catch {}
  }
  return null;
};

const endpoints = {
  list:    () => roots().map((r) => `${r}/addon-rules/`),
  options: () => roots().map((r) => `${r}/addon-rules/options/`),
  detail:  (uuid) => roots().map((r) => `${r}/addon-rules/${uuid}/`),
};

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

const toKey = (label) =>
  (label || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

const ActionBadge = ({ action }) => {
  const m = ACTION_META[action] || ACTION_META.MANUAL_REVIEW;
  return (
    <span style={{
      padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: m.bg, border: `1px solid ${m.border}`, color: m.text,
    }}>
      {m.label}
    </span>
  );
};

const LabeledInput = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>
      {label}{required && <span style={{ color: "#dc2626", marginLeft: 2 }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>{hint}</p>}
  </div>
);

const inputStyle = {
  width: "100%", padding: "7px 10px", border: "1px solid #cbd5e1",
  borderRadius: 6, fontSize: 13, color: "#1e293b", boxSizing: "border-box",
  background: "#fff",
};

const selectStyle = { ...inputStyle, cursor: "pointer" };

// ─── Toggle ──────────────────────────────────────────────────────────────────

const Toggle = ({ value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{
      width: 40, height: 22, borderRadius: 11, position: "relative", cursor: "pointer",
      background: value ? "#0891b2" : "#cbd5e1", transition: "background 0.2s",
      flexShrink: 0,
    }}
  >
    <div style={{
      position: "absolute", top: 3, left: value ? 21 : 3,
      width: 16, height: 16, borderRadius: "50%", background: "#fff",
      transition: "left 0.2s", boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    }} />
  </div>
);

// ─── Category chips ──────────────────────────────────────────────────────────

const CategoryChips = ({ value = [], onChange }) => {
  // Any category already on the rule that isn't one of the fixed named
  // options — e.g. a programme created later in the web portal, like
  // "OPD-FP" — gets its own removable chip here, so admins aren't limited
  // to the 4 hardcoded categories and can trigger addon rules off new
  // programmes without needing to call the API directly.
  const [customInput, setCustomInput] = useState("");
  const extras = value.filter((c) => !CATEGORY_OPTIONS.includes(c));

  const addCustom = () => {
    const trimmed = customInput.trim();
    if (!trimmed || value.includes(trimmed)) return;
    onChange([...value, trimmed]);
    setCustomInput("");
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {CATEGORY_OPTIONS.map((cat) => {
          const sel = value.includes(cat);
          return (
            <button key={cat} type="button"
              onClick={() => onChange(sel ? value.filter((c) => c !== cat) : [...value, cat])}
              style={{
                padding: "4px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer",
                border: sel ? "1px solid #0891b2" : "1px solid #cbd5e1",
                background: sel ? "#e0f2fe" : "#f8fafc",
                color: sel ? "#0c4a6e" : "#64748b", fontWeight: sel ? 600 : 400,
                transition: "all 0.15s",
              }}
            >
              {cat}
            </button>
          );
        })}
        {extras.map((cat) => (
          <button key={cat} type="button"
            onClick={() => onChange(value.filter((c) => c !== cat))}
            title="Click to remove"
            style={{
              padding: "4px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer",
              border: "1px solid #0891b2", background: "#e0f2fe", color: "#0c4a6e",
              fontWeight: 600, display: "flex", alignItems: "center", gap: 4,
            }}
          >
            {cat} <span aria-hidden="true">&times;</span>
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <input
          type="text"
          placeholder="Custom category (e.g. OPD-FP)"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          style={{
            padding: "4px 8px", fontSize: 13, border: "1px solid #cbd5e1",
            borderRadius: 6, flex: 1, minWidth: 160,
          }}
        />
        <button type="button" onClick={addCustom}
          style={{
            padding: "4px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer",
            border: "1px solid #0891b2", background: "#e0f2fe", color: "#0c4a6e", fontWeight: 600,
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
};

// ─── Required Fields Builder ─────────────────────────────────────────────────

const emptyField = () => ({
  _id: Math.random().toString(36).slice(2),
  key: "", label: "", type: "boolean", required: false,
  choices: "",
  visible_when_field: "", visible_when_op: "eq", visible_when_value: "",
});

const FieldRow = ({ field, onChange, onRemove }) => {
  const needsChoices = field.type === "select" || field.type === "multiselect";
  const update = (patch) => onChange({ ...field, ...patch });

  return (
    <div style={{
      border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px",
      background: "#f8fafc", marginBottom: 8,
    }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "start" }}>
        {/* Label */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Label *</label>
          <input
            value={field.label}
            onChange={(e) => {
              const label = e.target.value;
              update({ label, key: toKey(label) });
            }}
            placeholder="e.g. Complication Occurred"
            style={{ ...inputStyle, marginTop: 3 }}
          />
          {field.key && (
            <span style={{ fontSize: 10, color: "#94a3b8" }}>key: {field.key}</span>
          )}
        </div>

        {/* Type */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Type</label>
          <select value={field.type} onChange={(e) => update({ type: e.target.value })}
            style={{ ...selectStyle, marginTop: 3 }}>
            {FIELD_TYPES.map((t) => (
              <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>

        {/* Required + Remove */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, paddingTop: 18 }}>
          <label style={{ fontSize: 10, color: "#64748b", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <input type="checkbox" checked={!!field.required}
              onChange={(e) => update({ required: e.target.checked })} />
            Req.
          </label>
          <button onClick={onRemove} title="Remove field"
            style={{
              width: 26, height: 26, borderRadius: 6, border: "1px solid #fca5a5",
              background: "#fff", color: "#dc2626", cursor: "pointer", fontSize: 14,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>×</button>
        </div>
      </div>

      {/* Choices (for select / multiselect) */}
      {needsChoices && (
        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>
            Options <span style={{ fontWeight: 400 }}>(comma-separated)</span>
          </label>
          <input
            value={field.choices}
            onChange={(e) => update({ choices: e.target.value })}
            placeholder="Option A, Option B, Option C"
            style={{ ...inputStyle, marginTop: 3 }}
          />
        </div>
      )}

      {/* Visible when (optional condition) */}
      <div style={{ marginTop: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>
          Show only when <span style={{ fontWeight: 400 }}>(optional — hide this field unless another field matches)</span>
        </label>
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
          Enter the <strong>key</strong> of another field in this form (auto-generated from its label, e.g. <code>complications</code>). This field will only appear when that field's value matches.
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 3 }}>
          <input
            value={field.visible_when_field}
            onChange={(e) => update({ visible_when_field: e.target.value })}
            placeholder="other field's key  e.g. complications"
            style={{ ...inputStyle, flex: 1 }}
          />
          <select value={field.visible_when_op}
            onChange={(e) => update({ visible_when_op: e.target.value })}
            style={{ ...selectStyle, width: 64, flex: "none" }}>
            {OPERATORS.map((op) => (
              <option key={op.value} value={op.value}>{op.label}</option>
            ))}
          </select>
          <input
            value={field.visible_when_value}
            onChange={(e) => update({ visible_when_value: e.target.value })}
            placeholder="value"
            style={{ ...inputStyle, flex: 1 }}
          />
        </div>
      </div>
    </div>
  );
};

const fieldsToPayload = (fields) =>
  fields
    .filter((f) => f.label.trim())
    .map((f) => {
      const out = {
        key: f.key || toKey(f.label),
        label: f.label.trim(),
        type: f.type,
        required: !!f.required,
      };
      if ((f.type === "select" || f.type === "multiselect") && f.choices.trim()) {
        out.choices = f.choices.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (f.visible_when_field.trim()) {
        let val = f.visible_when_value.trim();
        if (val === "true") val = true;
        else if (val === "false") val = false;
        out.visible_when = [{
          field: f.visible_when_field.trim(),
          operator: f.visible_when_op,
          value: val,
        }];
      }
      return out;
    });

const payloadToFields = (fields) =>
  (fields || []).map((f) => {
    const vw = (f.visible_when || [])[0] || {};
    return {
      _id: Math.random().toString(36).slice(2),
      key: f.key || "",
      label: f.label || "",
      type: f.type || "boolean",
      required: !!f.required,
      choices: (f.choices || []).join(", "),
      visible_when_field: vw.field || "",
      visible_when_op: vw.operator || "eq",
      visible_when_value: vw.value != null ? String(vw.value) : "",
    };
  });

// ─── Service Multi-Select Picker ─────────────────────────────────────────────

const ServicePicker = ({ allServices, value = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q
      ? allServices.filter((s) => s.code.toLowerCase().includes(q) || s.name.toLowerCase().includes(q))
      : allServices;
  }, [allServices, search]);

  const toggle = (code) =>
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);

  const selectedServices = allServices.filter((s) => value.includes(s.code));

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger button */}
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          minHeight: 36, padding: "6px 10px", border: `1px solid ${open ? "#0891b2" : "#cbd5e1"}`,
          borderRadius: 6, cursor: "pointer", background: "#fff", display: "flex",
          flexWrap: "wrap", gap: 5, alignItems: "center",
          boxShadow: open ? "0 0 0 2px rgba(8,145,178,0.15)" : "none",
          transition: "border-color 0.15s",
        }}
      >
        {selectedServices.length === 0 ? (
          <span style={{ fontSize: 13, color: "#94a3b8" }}>Click to select services…</span>
        ) : (
          selectedServices.map((s) => (
            <span key={s.code} style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 8px", background: "#ecfdf5", border: "1px solid #a7f3d0",
              borderRadius: 4, fontSize: 12, fontWeight: 600, color: "#065f46",
              fontFamily: "monospace",
            }}>
              {s.code}
              <span
                onClick={(e) => { e.stopPropagation(); toggle(s.code); }}
                style={{ cursor: "pointer", color: "#6ee7b7", fontWeight: 700, fontSize: 13 }}
              >×</span>
            </span>
          ))
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#94a3b8" }}>
          {open ? "▲" : "▼"}
        </span>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", zIndex: 100, top: "calc(100% + 4px)", left: 0, right: 0,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 280, display: "flex", flexDirection: "column",
        }}>
          {/* Search */}
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code or name…"
              style={{ ...inputStyle, marginBottom: 0 }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* List */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 && (
              <div style={{ padding: "12px 12px", fontSize: 13, color: "#94a3b8", textAlign: "center" }}>
                No services match
              </div>
            )}
            {filtered.map((svc) => {
              const checked = value.includes(svc.code);
              return (
                <label key={svc.code} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                  cursor: "pointer", background: checked ? "#f0fdf4" : "transparent",
                  borderBottom: "1px solid #f8fafc",
                  transition: "background 0.1s",
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(svc.code)}
                    style={{ flexShrink: 0 }}
                  />
                  <span style={{
                    padding: "1px 6px", background: "#f1f5f9", borderRadius: 4,
                    fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: "#334155",
                    flexShrink: 0,
                  }}>
                    {svc.code}
                  </span>
                  <span style={{ fontSize: 13, color: "#1e293b" }}>{svc.name}</span>
                  {svc.type && (
                    <span style={{ marginLeft: "auto", fontSize: 10, color: "#94a3b8", flexShrink: 0 }}>
                      {svc.type}
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{
            padding: "8px 12px", borderTop: "1px solid #f1f5f9", display: "flex",
            justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              {value.length} selected
            </span>
            <button
              onClick={() => { onChange([]); }}
              style={{
                fontSize: 11, color: "#dc2626", background: "none", border: "none",
                cursor: "pointer", padding: 0,
              }}
            >
              Clear all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Create / Edit panel ──────────────────────────────────────────────────────

const RuleForm = ({ allItems, allServices, existingCodes, initial, onSave, onCancel }) => {
  const isEdit = !!initial?.uuid;

  const [itemId, setItemId] = useState(initial?.medical_item?.id?.toString() || "");
  const [name, setName] = useState(initial?.name || "");
  const [enabled, setEnabled] = useState(initial?.enabled !== false);
  const [action, setAction] = useState(initial?.system_action || "MANUAL_REVIEW");
  const [triggerCodes, setTriggerCodes] = useState(initial?.trigger_service_codes || []);
  const [categories, setCategories] = useState(initial?.trigger_categories || []);
  const [fields, setFields] = useState(payloadToFields(initial?.required_fields));
  const [maxQty, setMaxQty] = useState(
    initial?.common_rules?.max_quantity != null
      ? String(initial.common_rules.max_quantity)
      : "1"
  );
  const [requiresIndication, setRequiresIndication] = useState(
    !!initial?.common_rules?.requires_indication
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const selectedItem = allItems.find((i) => String(i.id) === itemId);

  // Filter the service picker to only show services matching the selected trigger categories.
  // If no categories are selected, show all services.
  // "Other" means show services whose code doesn't match any named-category prefix.
  const filteredServices = useMemo(() => {
    if (!categories.length) return allServices;
    const namedPrefixes = Object.values(CATEGORY_PREFIXES).map((p) => p.toLowerCase());
    // A custom/free-typed category (see CategoryChips) has no known code
    // prefix to filter by — treat it the same as "Other" (permissive)
    // rather than excluding every service, which would otherwise silently
    // empty this picker for any newly-created programme's addon rules.
    const hasUnknownCategory = categories.some((cat) => cat !== "Other" && !CATEGORY_PREFIXES[cat]);
    const permissive = categories.includes("Other") || hasUnknownCategory;
    return allServices.filter((s) => {
      const code = s.code.toLowerCase();
      if (permissive && !namedPrefixes.some((p) => code.startsWith(p))) return true;
      return categories.some((cat) => {
        const prefix = CATEGORY_PREFIXES[cat];
        return prefix && code.startsWith(prefix.toLowerCase());
      });
    });
  }, [allServices, categories]);

  useEffect(() => {
    if (!isEdit && selectedItem && !name) {
      setName(`${selectedItem.code} – ${selectedItem.name}`);
    }
  }, [selectedItem, isEdit, name]);

  const handleSave = async () => {
    if (!isEdit && !itemId) { setErr("Select a medical item."); return; }
    setErr("");
    setSaving(true);

    const payload = {
      name: name.trim() || (selectedItem ? `${selectedItem.code} – ${selectedItem.name}` : ""),
      enabled,
      system_action: action,
      trigger_service_codes: triggerCodes,
      trigger_categories: categories,
      required_fields: fieldsToPayload(fields),
      common_rules: {
        requires_indication: requiresIndication,
        requires_attachments: fields.some((f) => f.type === "attachment"),
        max_quantity: parseInt(maxQty, 10) || 1,
      },
    };
    if (!isEdit) payload.medical_item_id = itemId;

    const eps = isEdit ? endpoints.detail(initial.uuid) : endpoints.list();
    const method = isEdit ? "PUT" : "POST";

    try {
      const res = await tryFetch(eps, {
        method,
        headers: headers(),
        body: JSON.stringify(payload),
      });
      if (!res) { setErr("Could not reach server."); return; }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setErr(d.detail || `Error ${res.status}`);
        return;
      }
      const saved = await res.json();
      onSave(saved);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (idx, patch) =>
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeField = (idx) =>
    setFields((prev) => prev.filter((_, i) => i !== idx));

  const availableItems = isEdit
    ? allItems
    : allItems.filter(
        (i) => !existingCodes.includes(i.code) || String(i.id) === itemId
      );

  return (
    <div style={{
      background: "#fff", border: "1px solid #bae6fd", borderRadius: 10,
      boxShadow: "0 4px 16px rgba(8,145,178,0.08)", marginBottom: 20,
    }}>
      {/* Panel header */}
      <div style={{
        background: "#f0f9ff", padding: "14px 20px",
        borderBottom: "1px solid #bae6fd", borderRadius: "10px 10px 0 0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: "#0c4a6e" }}>
          {isEdit ? `Edit — ${initial.addon_code}` : "New ADDON Rule"}
        </span>
        <button onClick={onCancel}
          style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>
          ×
        </button>
      </div>

      <div style={{ padding: "20px 20px 0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

          {/* LEFT column */}
          <div>
            {/* Medical item picker */}
            {!isEdit ? (
              <LabeledInput label="Medical Item" required hint="Choose the medical item whose code becomes the ADDON code (e.g. ADD001).">
                <select value={itemId} onChange={(e) => { setItemId(e.target.value); setName(""); }}
                  style={selectStyle}>
                  <option value="">— select item —</option>
                  {availableItems.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.code} – {i.name}
                    </option>
                  ))}
                </select>
              </LabeledInput>
            ) : (
              <LabeledInput label="Medical Item">
                <div style={{
                  padding: "7px 10px", background: "#f1f5f9", borderRadius: 6,
                  fontSize: 13, color: "#475569", border: "1px solid #e2e8f0",
                }}>
                  {initial.addon_code}
                  {initial.medical_item && ` – ${initial.medical_item.name}`}
                </div>
              </LabeledInput>
            )}

            {/* Name */}
            <LabeledInput label="Display Name">
              <input value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Auto-filled from item" style={inputStyle} />
            </LabeledInput>

            {/* Enabled */}
            <LabeledInput label="Status">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Toggle value={enabled} onChange={setEnabled} />
                <span style={{ fontSize: 13, color: "#1e293b" }}>
                  {enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            </LabeledInput>

            {/* System action */}
            <LabeledInput label="System Action">
              <select value={action} onChange={(e) => setAction(e.target.value)} style={selectStyle}>
                {SYSTEM_ACTIONS.map((a) => (
                  <option key={a} value={a}>{ACTION_META[a].label}</option>
                ))}
              </select>
            </LabeledInput>

            {/* Trigger categories — select first; service list filters based on this */}
            <LabeledInput label="Trigger Categories" hint="Select categories first — the service picker below will filter to matching codes only.">
              <CategoryChips value={categories} onChange={(val) => { setCategories(val); setTriggerCodes([]); }} />
            </LabeledInput>

            {/* Trigger service codes — filtered by the selected categories above */}
            <LabeledInput
              label="Trigger Service Codes"
              hint={
                categories.length
                  ? `Showing ${filteredServices.length} service(s) matching selected categories. Leave empty to trigger for all.`
                  : "Select categories above to filter this list, or leave empty to apply to all services."
              }
            >
              <ServicePicker
                allServices={filteredServices}
                value={triggerCodes}
                onChange={setTriggerCodes}
              />
            </LabeledInput>

            {/* Common rules */}
            <LabeledInput label="Business Rules">
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={requiresIndication}
                    onChange={(e) => setRequiresIndication(e.target.checked)} />
                  Requires written indication
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <label style={{ fontSize: 13, color: "#1e293b", whiteSpace: "nowrap" }}>Max quantity:</label>
                  <input
                    type="number" min={1} value={maxQty}
                    onChange={(e) => setMaxQty(e.target.value)}
                    style={{ ...inputStyle, width: 80 }}
                  />
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    Set 99 if unlimited (e.g. blood transfusion)
                  </span>
                </div>
              </div>
            </LabeledInput>
          </div>

          {/* RIGHT column — required fields */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Required Fields (tablet form)
              </span>
              <button
                onClick={() => setFields((prev) => [...prev, emptyField()])}
                style={{
                  padding: "4px 12px", background: "#0891b2", color: "#fff",
                  border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                + Add Field
              </button>
            </div>

            <div style={{ maxHeight: 460, overflowY: "auto", paddingRight: 4 }}>
              {fields.length === 0 && (
                <div style={{
                  padding: "24px", textAlign: "center", background: "#f8fafc",
                  borderRadius: 8, border: "1px dashed #cbd5e1", color: "#94a3b8", fontSize: 13,
                }}>
                  No fields yet. Click <strong>+ Add Field</strong> to start.
                </div>
              )}
              {fields.map((f, idx) => (
                <FieldRow
                  key={f._id}
                  field={f}
                  onChange={(patch) => updateField(idx, patch)}
                  onRemove={() => removeField(idx)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Error */}
        {err && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6,
            padding: "8px 12px", color: "#991b1b", fontSize: 13, marginTop: 12,
          }}>
            {err}
          </div>
        )}

        {/* Footer actions */}
        <div style={{
          display: "flex", gap: 10, padding: "16px 0",
          borderTop: "1px solid #e2e8f0", marginTop: 16,
        }}>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "8px 22px", background: saving ? "#94a3b8" : "#0891b2",
            color: "#fff", border: "none", borderRadius: 6, fontWeight: 600,
            fontSize: 14, cursor: saving ? "not-allowed" : "pointer",
          }}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create ADDON Rule"}
          </button>
          <button onClick={onCancel} style={{
            padding: "8px 16px", background: "transparent",
            border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14,
            color: "#64748b", cursor: "pointer",
          }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ADDON Card (view + edit inline) ─────────────────────────────────────────

const AddonCard = ({ rule, allItems, allServices, existingCodes, onUpdated, onDeleted }) => {
  const [editing, setEditing]     = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting]   = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await tryFetch(endpoints.detail(rule.uuid), {
        method: "DELETE", headers: headers(),
      });
      if (res && (res.ok || res.status === 204)) {
        onDeleted(rule.uuid);
      } else {
        const d = await res?.json().catch(() => ({}));
        alert(d?.detail || "Delete failed");
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  };

  if (editing) {
    return (
      <RuleForm
        allItems={allItems}
        allServices={allServices}
        existingCodes={existingCodes}
        initial={rule}
        onSave={(updated) => { onUpdated(updated); setEditing(false); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
      marginBottom: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      overflow: "hidden",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "13px 16px",
      }}>
        {/* Enabled dot */}
        <span title={rule.enabled ? "Enabled" : "Disabled"} style={{
          width: 9, height: 9, borderRadius: "50%",
          background: rule.enabled ? "#10b981" : "#d1d5db", flexShrink: 0,
        }} />

        {/* Code chip */}
        <span style={{
          padding: "2px 9px", background: "#0891b2", color: "#fff",
          borderRadius: 5, fontWeight: 700, fontSize: 13,
          fontFamily: "monospace", flexShrink: 0,
        }}>
          {rule.addon_code}
        </span>

        {/* Name */}
        <span style={{ fontWeight: 600, fontSize: 14, color: "#1e293b", flex: 1 }}>
          {rule.medical_item ? rule.medical_item.name : rule.name}
        </span>

        {/* Categories */}
        <div style={{ display: "flex", gap: 4 }}>
          {(rule.trigger_categories || []).map((c) => (
            <span key={c} style={{
              padding: "1px 7px", background: "#f1f5f9", border: "1px solid #cbd5e1",
              borderRadius: 4, fontSize: 11, color: "#475569", fontWeight: 500,
            }}>{c}</span>
          ))}
        </div>

        {/* System action */}
        <ActionBadge action={rule.system_action} />

        {/* Field count */}
        <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>
          {(rule.required_fields || []).length} fields
        </span>

        {/* Actions */}
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button onClick={() => setEditing(true)} style={{
            padding: "4px 12px", background: "#f0f9ff", border: "1px solid #bae6fd",
            borderRadius: 5, fontSize: 12, color: "#0891b2", cursor: "pointer", fontWeight: 500,
          }}>
            Edit
          </button>

          {!confirming ? (
            <button onClick={() => setConfirming(true)} style={{
              padding: "4px 10px", background: "#fff", border: "1px solid #fca5a5",
              borderRadius: 5, fontSize: 12, color: "#dc2626", cursor: "pointer",
            }}>
              Delete
            </button>
          ) : (
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#64748b" }}>Sure?</span>
              <button onClick={handleDelete} disabled={deleting} style={{
                padding: "3px 8px", background: "#dc2626", border: "none",
                borderRadius: 4, fontSize: 11, color: "#fff", cursor: "pointer",
              }}>
                {deleting ? "…" : "Yes"}
              </button>
              <button onClick={() => setConfirming(false)} style={{
                padding: "3px 8px", background: "#f1f5f9", border: "1px solid #e2e8f0",
                borderRadius: 4, fontSize: 11, cursor: "pointer",
              }}>
                No
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Trigger codes preview */}
      {(rule.trigger_service_codes || []).length > 0 && (
        <div style={{
          padding: "6px 16px 10px 46px", display: "flex", gap: 5, flexWrap: "wrap",
        }}>
          {rule.trigger_service_codes.map((c) => (
            <span key={c} style={{
              padding: "1px 7px", background: "#ecfdf5", border: "1px solid #a7f3d0",
              borderRadius: 4, fontSize: 11, color: "#065f46", fontFamily: "monospace",
            }}>{c}</span>
          ))}
          {(rule.required_fields || []).length > 0 && (
            <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 4, alignSelf: "center" }}>
              · {(rule.required_fields || []).map((f) => f.label).join(", ")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AddonRulesManagerPage = () => {
  const [rules, setRules]       = useState([]);
  const [items, setItems]       = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]     = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rulesRes, itemsRes] = await Promise.all([
        tryFetch(endpoints.list(), { headers: headers() }),
        tryFetch(endpoints.options(), { headers: headers() }),
      ]);

      if (!rulesRes) { setError("Cannot reach addon-rules endpoint."); return; }
      if (!rulesRes.ok) { setError(`Server error ${rulesRes.status}`); return; }

      const rd = await rulesRes.json();
      setRules(rd.addon_rules || []);

      if (itemsRes && itemsRes.ok) {
        const id = await itemsRes.json();
        setItems(id.items || []);
        setServices(id.services || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const existingCodes = rules.map((r) => r.addon_code).filter(Boolean);

  const handleCreated = (rule) => {
    setRules((prev) => [...prev, rule].sort((a, b) => a.addon_code.localeCompare(b.addon_code)));
    setCreating(false);
  };

  const handleUpdated = (updated) =>
    setRules((prev) => prev.map((r) => (r.uuid === updated.uuid ? updated : r)));

  const handleDeleted = (uuid) =>
    setRules((prev) => prev.filter((r) => r.uuid !== uuid));

  return (
    <div style={{
      padding: 24, maxWidth: 1060, margin: "0 auto",
      // This page lets admins enter/preview optional Myanmar field labels —
      // "Noto Sans" (generic) has no Myanmar coverage and isn't loaded as a
      // web font anyway, so Myanmar glyphs here fell through to whatever the
      // OS happened to provide. Pyidaungsu (index.css @font-face) fixes that.
      fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, Pyidaungsu, sans-serif",
      color: "#1e293b",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>ADDON Rules</h1>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748b" }}>
            Claim add-on packages driven by medical items. Each ADDON code must exist as a medical item in OpenIMIS.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} disabled={loading} style={{
            padding: "7px 14px", background: "#f8fafc", border: "1px solid #cbd5e1",
            borderRadius: 6, fontSize: 13, color: "#475569", cursor: loading ? "not-allowed" : "pointer",
          }}>
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
          {!creating && (
            <button onClick={() => setCreating(true)} style={{
              padding: "7px 16px", background: "#0891b2", color: "#fff",
              border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}>
              + Add ADDON Rule
            </button>
          )}
        </div>
      </div>

      {/* Info banner */}
      <div style={{
        background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8,
        padding: "10px 14px", fontSize: 13, color: "#0c4a6e", marginBottom: 20,
        display: "flex", gap: 10,
      }}>
        <span style={{ flexShrink: 0 }}>ℹ</span>
        <span>
          Only medical items with <strong>Item Type = <code style={{ background: "#e0f2fe", borderRadius: 3, padding: "1px 4px" }}>O</code></strong>{" "}
          (Other) appear in the picker — name and code don't matter.
          To add a new ADDON package, create a medical item in the admin with type O.
          ADDONs are triggered by the{" "}
          <strong>primary service category</strong> on the claim — not by diagnosis.
        </span>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8,
          padding: "12px 16px", color: "#991b1b", fontSize: 14, marginBottom: 16,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span>{error}</span>
          <button onClick={load} style={{
            padding: "4px 10px", background: "#fff", border: "1px solid #fca5a5",
            borderRadius: 4, cursor: "pointer", fontSize: 12, color: "#991b1b",
          }}>Retry</button>
        </div>
      )}

      {/* Create form */}
      {creating && (
        <RuleForm
          allItems={items}
          allServices={services}
          existingCodes={existingCodes}
          initial={null}
          onSave={handleCreated}
          onCancel={() => setCreating(false)}
        />
      )}

      {/* Loading skeletons */}
      {loading && !error && [...Array(4)].map((_, i) => (
        <div key={i} style={{
          height: 52, background: "#f1f5f9", borderRadius: 10, marginBottom: 10,
          animation: "pulse 1.4s ease-in-out infinite",
        }} />
      ))}

      {/* Rules list */}
      {!loading && !error && (
        <>
          {rules.length > 0 && (
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10, display: "flex", gap: 16 }}>
              <span>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#10b981", marginRight: 5 }} />
                {rules.filter((r) => r.enabled).length} enabled
              </span>
              <span>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#d1d5db", marginRight: 5 }} />
                {rules.filter((r) => !r.enabled).length} disabled
              </span>
              <span style={{ marginLeft: "auto" }}>
                {rules.length} total · click <strong>Edit</strong> to configure
              </span>
            </div>
          )}

          {rules.map((rule) => (
            <AddonCard
              key={rule.uuid}
              rule={rule}
              allItems={items}
              allServices={services}
              existingCodes={existingCodes}
              onUpdated={handleUpdated}
              onDeleted={handleDeleted}
            />
          ))}

          {rules.length === 0 && !creating && (
            <div style={{
              textAlign: "center", padding: "48px 24px", background: "#f8fafc",
              borderRadius: 10, border: "1px dashed #cbd5e1",
            }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
              <p style={{ fontSize: 15, fontWeight: 600, color: "#475569", margin: 0 }}>
                No ADDON rules configured yet
              </p>
              <p style={{ fontSize: 13, color: "#94a3b8", margin: "6px 0 16px" }}>
                Create medical items with codes like ADD001, ADD002, then add rules here.
              </p>
              <button onClick={() => setCreating(true)} style={{
                padding: "8px 20px", background: "#0891b2", color: "#fff",
                border: "none", borderRadius: 6, cursor: "pointer",
                fontWeight: 600, fontSize: 14,
              }}>
                + Add ADDON Rule
              </button>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.45} }
        select,input { outline: none; }
        select:focus,input:focus { border-color: #0891b2 !important; box-shadow: 0 0 0 2px rgba(8,145,178,0.15); }
      `}</style>
    </div>
  );
};

export default AddonRulesManagerPage;
